import { IconButton, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, useTheme } from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import { AddSmallPrimaryTextButton } from 'framework/components/buttons/AddSmallPrimaryTextButton';
import { DividerWithCaption } from 'framework/components/DividerWithCaption';
import { LabelledProperty } from 'framework/components/labelledProperty/LabelledProperty';
import { NoRecordsFound } from 'framework/components/NoRecordsFound';
import { AlertDialog } from 'framework/dialogs/AlertDialog';
import { usePreloadCacheContext } from 'framework/hooks/usePreloadCacheContext';
import { annotationTypesCommand_delete, IAnnotationType } from 'gen/ApiClient';
import { useLocalization } from 'localization/useLocalization';
import React, { useMemo } from 'react';
import { useDialogsContext } from '../../dialogs/useDialogsContext';
import { AnnotationTypesCacheContext } from '../context/AnnotationTypesCacheContext';
import { AnnotationsColorPickerWrapper } from './AnnotationsColorPickerWrapper';
import { AnnotationTypeForm } from './AnnotationTypeForm';

interface IProps {
	isFollowUp: boolean;
}

export const TypesList = ({ isFollowUp }: IProps) => {
	const strings = useLocalization();
	const [allTypes, load] = usePreloadCacheContext(AnnotationTypesCacheContext);
	const types = useMemo(() => allTypes.filter(t => t.isDeleted === false && t.isFollowUp === isFollowUp), [allTypes, isFollowUp]);
	const { open, confirm, cancel } = useDialogsContext(load);
	const theme = useTheme();

	const onNew = () => {
		open(
			<AnnotationTypeForm
				open
				confirm={confirm}
				cancel={cancel}
				isFollowUp={isFollowUp}
			/>
		);
	};

	const onDelete = (id: string) => {
		open(
			<AlertDialog
				open
				content={strings.deleteWhatQuestion(strings.typeAnnotation)}
				acceptText={strings.yesCommaDelete}
				rejectText={strings.noCommaGoBack}
				reject={cancel}
				acceptF={() => annotationTypesCommand_delete(id)}
				accept={confirm}
			/>
		);
	};

	const onEdit = (val: IAnnotationType) => {
		open(
			<AnnotationTypeForm
				open
				confirm={confirm}
				cancel={cancel}
				annotationType={val}
				isFollowUp={isFollowUp}
			/>
		);
	};

	return (
		<>
			{types && (
				<div style={{ marginBottom: theme.spacing(4) }}>
					<DividerWithCaption
						caption={strings.typesAnnotation}
						captionStyle={{ fontWeight: 500, color: 'black' }}
						action={<AddSmallPrimaryTextButton onClick={onNew}>{strings.add}</AddSmallPrimaryTextButton>}
					/>
					{types.length !== 0 && (
						<>
							<List
								dense
								disablePadding>
								{types.map(t => (
									<SingleItem
										key={t.id}
										t={t}
										load={load}
										onEdit={() => onEdit(t)}
										onDelete={() => onDelete(t.id)}
									/>
								))}
							</List>
						</>
					)}
					{types.length === 0 && <NoRecordsFound>{strings.noTypesAnnotationDefined}</NoRecordsFound>}
				</div>
			)}
		</>
	);
};

interface ISingleItemProps {
	t: IAnnotationType;
	load: VoidFunction;
	onEdit: VoidFunction;
	onDelete: VoidFunction;
}

const SingleItem = ({ t, load, onEdit, onDelete }: ISingleItemProps) => {
	const strings = useLocalization();

	return (
		<ListItem
			key={t.id}
			disableGutters>
			<ListItemIcon style={{ paddingLeft: '11px' }}>
				<AnnotationsColorPickerWrapper
					annotationType={t}
					reload={load}
				/>
			</ListItemIcon>
			<ListItemText
				primary={
					<LabelledProperty
						withoutMargin
						label={strings.name}
						property={t.name}
					/>
				}
			/>
			<ListItemSecondaryAction>
				<IconButton
					onClick={onEdit}
					edge='end'>
					<EditOutlinedIcon />
				</IconButton>
				<IconButton
					onClick={onDelete}
					edge='end'>
					<DeleteOutlineIcon />
				</IconButton>
			</ListItemSecondaryAction>
		</ListItem>
	);
};
